import {Component, OnDestroy, OnInit} from '@angular/core';
import {Observable}                   from 'rxjs';
import {ApGridPagerType, IGridData}   from '../../../ap-interface';
import {
  ApDynGridColumnConfigBase
}                                     from '../../../ap-dyngrids/config/ap-dyn-grid-column-config-base';
import {
  ApDynGridPropertyColumnConfig
}                                     from '../../../ap-dyngrids/config/ap-dyn-grid-property-column-config';
import {SortDescriptor}               from '@progress/kendo-data-query';
import {
  ApDynGridPagerConfigBase,
  ApDynGridPagerControlType
}                                     from '../../../ap-dyngrids/config/ap-dyn-grid-pager-config-base';
import {
  MapViewStore
}                                     from '../../../stores/layout/mapview.store';
import {
  LanguageStore
}                                     from '../../../stores/translation/language.store';
import {filter, map}                  from 'rxjs/operators';
import {
  ActiveUsersStore
}                                     from '../../../stores/administration/active-users.store';
import {
  ApDynGridButtonColumnConfig
}                                     from '../../../ap-dyngrids/config/ap-dyn-grid-button-column-config';
import {
  ApUtilService
}                                     from '../../../ap-utils/service/ap-util.service';
import {
  ApDynGridPagerConfig,
  IDynGridWizardChoiceData,
  IGridPagerSubmitData
}                                     from '../../../ap-dyngrids/config/ap-dyn-grid-pager-config';
import {
  ModalDialogStore
}                                     from '../../../stores/dialog/modal.dialog.store';
import {
  IModalDialogData,
  IModalDialogResult
}                                     from '../../../ap-interface/interfaces/ap-modaldialog-data.interface';
import {
  NotificationStore
}                                     from '../../../stores/common/notification.store';
import {
  ApDynformsConfigFieldset
}                                     from '../../../ap-dynforms/config/ap-dynforms-config-fieldset';
import {ApGuidUtil}                   from '../../../ap-utils';
import {
  ApDynformsConfigTextbox
}                                     from '../../../ap-dynforms/config/ap-dynforms-config-textbox';
import {LoginStore}                   from '../../../stores/login/login.store';
import IActiveUser = Data.Authentication.IActiveUser;
import INotifications = Data.Common.INotifications;

interface IActiveUserGridData extends IGridData<IActiveUser> {
  Icon: string;
  MessageIcon: string;
}

@Component({
  selector: 'ap-active-users',
  template: `
    <ap-dyncomponent [caption]="'Global__Active_Users'"
                     [canSearch]="true"
                     [canCreate]="false"
                     [columns]="columns"
                     [headerIcon]="'ap-basedata-icon'"
                     [items]="items$ | async"
                     [loading$]="loading$"
                     [pager]="pager"
                     (edit)="kickUser($event)"></ap-dyncomponent>`,
})
export class ApActiveUsersComponent implements OnInit, OnDestroy {
  public loading$: Observable<boolean>;
  public subscriptions = [];
  public columns: ApDynGridColumnConfigBase[];
  public items$: Observable<IActiveUserGridData[]>;
  public pager: ApDynGridPagerConfigBase;
  public sort: SortDescriptor[];

  private _loadInterval: any;
  private _farmId: number;
  private _userId: number;

  /**
   * Constructor
   */
  constructor(private loginStore: LoginStore,
              private activeUsersStore: ActiveUsersStore,
              private mapViewStore: MapViewStore,
              private languageStore: LanguageStore,
              private modalDialogStore: ModalDialogStore,
              private notificationStore: NotificationStore) {
    this.mapViewStore.hideMapView();
    this.loading$ = this.activeUsersStore.Listen(s => s.loading);
    this.items$ = this.activeUsersStore.Listen(s => s.data).pipe(
      filter(d => !!d && !!this.loginStore.SelectedFarmId),
      map(d => d.filter(user => user.FarmId === this.loginStore.SelectedFarmId).map(activeUser => {
        const user: IActiveUserGridData = {
          Id: activeUser.User.Id,
          Icon: 'logoutIcon',
          SourceItem: activeUser,
          MessageIcon: 'envelopIcon'
        };
        return user;
      })));
  }

  public ngOnInit(): void {
    this._buildGrid();
    setTimeout(() => {
      this._loadInterval = setInterval(() => {
        this.activeUsersStore.refreshActiveUsers();
      }, 5000);
    }, 5000);
    this.activeUsersStore.loadActiveUsers();

    this.subscriptions.push(this.modalDialogStore.Listen(s => s.result).subscribe(c => this._modalDialogResult(c)));
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach(d => {
      d.unsubscribe();
    });
    clearInterval(this._loadInterval);
  }

  /**
   * Handler for logging off user.
   */
  public kickUser(dataItem: IActiveUserGridData | IGridPagerSubmitData): void {
    const singleUser = dataItem as IActiveUserGridData;
    if (singleUser && singleUser.SourceItem && singleUser.SourceItem.ClientIp && singleUser.Id) {
      this.activeUsersStore.kickUser(singleUser.Id as number);
    } else {
      const multiUser = dataItem as IGridPagerSubmitData;
      if (multiUser && multiUser.ids && multiUser.ids.length > 0) {
        multiUser.ids.forEach(userId => {
          this.activeUsersStore.kickUser(userId);
        });
      }
    }
    this.activeUsersStore.refreshActiveUsers();
  }

  private _buildGrid(): void {
    this.columns = [
      new ApDynGridPropertyColumnConfig({
        field: 'SourceItem.User.Id',
        title: 'Global__User_Id',
        headerFilterable: true,
        filterable: true,
        width: 120
      }),
      new ApDynGridPropertyColumnConfig({
        field: 'SourceItem.User.UserName',
        title: 'Global__Username',
        headerFilterable: true,
        width: 150,
        sortIndex: 1,
        sortDesc: true
      }),
      new ApDynGridPropertyColumnConfig({
        field: 'SourceItem.UpdatedAt',
        title: 'Global__Last_Activity_utc',
        headerFilterable: true,
        format: this.languageStore.CurrentDateTimeFormat,
        width: 150
      }),
      new ApDynGridPropertyColumnConfig({
        field: 'SourceItem.CreatedAt',
        title: 'Global__Logintime_utc',
        headerFilterable: true,
        format: this.languageStore.CurrentDateTimeFormat,
        width: 150
      }),
      new ApDynGridPropertyColumnConfig({
        field: 'SourceItem.ClientIp',
        title: 'Global__Client_Ip',
        headerFilterable: true,
        width: 150
      }),
      new ApDynGridPropertyColumnConfig({
        field: 'SourceItem.FarmId',
        title: 'Global__Farm_Id',
        headerFilterable: true,
        width: 90
      }),
      new ApDynGridPropertyColumnConfig({
        field: 'SourceItem.CampaignYear',
        title: 'Base__HarvestYear',
        headerFilterable: true,
        width: 50
      }),
      new ApDynGridPropertyColumnConfig({
        field: 'SourceItem.CurrentModule',
        title: 'Global__Path',
        headerFilterable: true,
        width: 150
      }),
      new ApDynGridButtonColumnConfig({
        callback: ApUtilService.createEventEmitter(this._showDialog.bind(this), this.subscriptions),
        standardIcon: 'MessageIcon',
        isStandardIcon: true,
        tooltip: 'Global_Notifications_New'
      }),
      new ApDynGridButtonColumnConfig({
        callback: ApUtilService.createEventEmitter(this.kickUser.bind(this), this.subscriptions),
        standardIcon: 'Icon',
        isStandardIcon: true,
        tooltip: 'Admin_Pages__Logout'
      })
    ];
    this.pager = new ApDynGridPagerConfig({
      listMassEditing: [
        {
          id: 0,
          name: 'Admin_Pages__Logout',
          type: ApGridPagerType.None,
          onSubmit: ApUtilService.createEventEmitter(this.kickUser.bind(this), this.subscriptions)
        } as IDynGridWizardChoiceData,
      ]
    });
    this.pager.type = ApDynGridPagerControlType.Pager;
  }

  private _showDialog(dataItem: IActiveUserGridData): void {
    this._farmId = dataItem.SourceItem.FarmId;
    this._userId = dataItem.SourceItem.User.Id;
    const dialog = {
      title: '',
      formConfig: {
        fieldSets: [
          new ApDynformsConfigFieldset({
            key: 'messageBox',
            columns: 1,
            config: [
              new ApDynformsConfigTextbox({
                key: 'title',
                label: 'Titel',
                value: null
              }),
              new ApDynformsConfigTextbox({
                key: 'message',
                label: 'Message',
                value: null
              })
            ]
          })
        ],
        values: {
          Title: '',
          Message: '',
        },
        validators: []
      },
      show: true,
      buttons: [
        {
          key: 'cancel',
          text: 'Global__Cancel',
          primary: false
        },
        {
          key: 'apply',
          text: 'Global__Take',
          primary: true
        }
      ],
    } as IModalDialogData;
    this.modalDialogStore.setModalDialogData(dialog);
  }

  private _modalDialogResult(result: IModalDialogResult): void {
    if (result.key !== 'apply') {
      return;
    }
    const ets = {
      FarmId: this._farmId,
      Caption: result.formValues.title,
      Description: result.formValues.message,
      UserId: this._userId,
      Id: ApGuidUtil.generateNewGuid(),
      Link: '',
      Version: 0,
      WasRead: false,
      Timestamp: new Date(),
      ChangedAt: null,
      ChangedBy: null,
      CreatedAt: null,
      CreatedBy: null,
      DeletedAt: null,
      DeletedBy: null
    } as INotifications;
    this.notificationStore.insertNotification(ets);
  }
}
